/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { hashKey, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { debounce, type DebouncedFunc } from 'lodash';

// Debounce window: a burst of WS events for the same key collapses into one refetch.
const DEBOUNCE_MS = 500;

// Hard ceiling so a continuous event stream can't starve the debouncer forever.
const MAX_WAIT_MS = 5_000;

// After a successful refetch, suppress further invalidations for this key.
// Prevents a flood of WS events from hammering the API even when responses succeed.
const SUCCESS_COOLDOWN_MS = 5_000;

// If a scheduled refetch fails, ignore further invalidations for this key for this long.
// Prevents a timing-out API from being hammered as fast as WS events arrive.
const FAILURE_BACKOFF_MS = 10_000;

const debouncersByKey = new Map<string, DebouncedFunc<() => void>>();
// Backoff re-arm timers are raw setTimeouts; track them so tests can cancel them.
const backoffTimers = new Set<ReturnType<typeof setTimeout>>();

function buildDebouncer(queryClient: QueryClient, queryKey: QueryKey): DebouncedFunc<() => void> {
  let failedAt = 0;
  let succeededAt = 0;

  const runOrDefer = async (): Promise<void> => {
    const now = Date.now();

    const failCooldown = FAILURE_BACKOFF_MS - (now - failedAt);
    if (failCooldown > 0) {
      const timer = setTimeout(() => {
        backoffTimers.delete(timer);
        debouncer();
      }, failCooldown + 1);
      backoffTimers.add(timer);
      return;
    }

    const successCooldown = SUCCESS_COOLDOWN_MS - (now - succeededAt);
    if (successCooldown > 0) {
      const timer = setTimeout(() => {
        backoffTimers.delete(timer);
        debouncer();
      }, successCooldown + 1);
      backoffTimers.add(timer);
      return;
    }

    try {
      await queryClient.invalidateQueries({ queryKey });
      const anyError = queryClient
        .getQueryCache()
        .findAll({ queryKey })
        .some((query) => query.state.status === 'error');
      if (anyError) {
        failedAt = Date.now();
      } else {
        succeededAt = Date.now();
      }
    } catch {
      failedAt = Date.now();
    }
  };

  const debouncer = debounce(runOrDefer, DEBOUNCE_MS, { maxWait: MAX_WAIT_MS });
  return debouncer;
}

/**
 * Debounced, backoff-aware wrapper around `queryClient.invalidateQueries`.
 *
 * Intended for WebSocket-driven invalidations where a single logical change
 * can emit many events in quick succession.
 */
export function scheduleInvalidate(queryClient: QueryClient, queryKey: QueryKey): void {
  const hash = hashKey(queryKey);
  let debouncer = debouncersByKey.get(hash);
  if (!debouncer) {
    debouncer = buildDebouncer(queryClient, queryKey);
    debouncersByKey.set(hash, debouncer);
  }
  debouncer();
}

/** Test helper: cancels pending debounces and backoff re-arm timers. */
export function resetInvalidationSchedulerForTests(): void {
  debouncersByKey.forEach((debouncer) => debouncer.cancel());
  debouncersByKey.clear();
  backoffTimers.forEach((timer) => clearTimeout(timer));
  backoffTimers.clear();
}
